import { Injectable, OnDestroy } from "@angular/core";
import { UserInfoViewModel, UserInfoData, FurtherAddress } from "../../shared/models/userInfo.model";
import { CircularInjectionHelper } from "../../services/circularInjectionHelper";
import { WebshopOrderDataViewModel, FurtherAddressViewModel } from "../../shared/models/webshop.model";
import { Subscription, Subject, Observable } from "rxjs";

@Injectable({
  providedIn: "root"
})
export class UserProfileService implements OnDestroy {

  /** Bindung an das User Information Change Event */
  private _observeUserDataChangedSubscription: Subscription;

  constructor(private _circularInjectionHelper: CircularInjectionHelper) {
    const me = this;
    // yourAppServer user ObserveUserDataChanged event wird weitergereicht
    me._observeUserDataChangedSubscription = me._circularInjectionHelper.yourAppServer.User.ObserveUserDataChanged.subscribe((userInfoData) => {
      me.TriggerUserDataChanged(userInfoData);
    });
  }

  ngOnDestroy(): void {
    const me = this;
    if (me._observeUserDataChangedSubscription) {
      me._observeUserDataChangedSubscription.unsubscribe();
    }
  }

  public GetWebshopOrderDataViewModel(): Promise<WebshopOrderDataViewModel> {
    const me = this;
    return new Promise((resolve, reject) => {
      me._circularInjectionHelper.yourAppServer.User.GetUserInfo()
        .then((userInfo) => {
          resolve(me.mapToWebshopOrderDataViewModel(userInfo));
        })
        .catch((error) => {
          me._circularInjectionHelper.coreLogService.error("UserProfileService.GetWebshopOrderDataViewModel()", "objObsInjectHelper.yourAppServer.User.GetUserInfo()", error);
          reject();
        });
    });
  }

  /** Use TriggerUserDataChanged for trigger */
  private _userDataChanged: Subject<UserInfoData> = new Subject();
  /** Triggert das ObserveUserDataChanged event, wenn die UserInformation geändert werden */
  private TriggerUserDataChanged(userInfoData: UserInfoData): void {
    this._userDataChanged.next(userInfoData);
  }
  /** Wird ausgelöst, wenn die UserInformation sich ändert und an den Server gesendet wurde */
  public get ObserveUserDataChanged(): Observable<UserInfoData> {
    return this._userDataChanged;
  }

  public GetUserInfoViewModel(): Promise<UserInfoViewModel> {
    const me = this;
    return new Promise((resolve, reject) => {
      me._circularInjectionHelper.yourAppServer.User.GetUserInfo(true)
        .then((userInfo) => {
          resolve(me.mapToUserInfoViewModel(userInfo));
        })
        .catch((error) => {
          reject();
          this._circularInjectionHelper.coreLogService.error("UserHelperService.GetUserInfoViewModel", "_circularInjectionHelper.yourAppServer.User.GetUserInfo()", error);
        });
    });
  }

  public logOut(): Promise<boolean> {
    const me = this;
    return new Promise((resolve, reject) => {
      me._circularInjectionHelper.yourAppServer.Auth.SignOut()
        .then((response) => {
          if (response) {
            resolve(true);
          } else {
            resolve(false);
          }
          me._circularInjectionHelper.yourAppServer.Auth.SignInAnonymousOrNobodyIfNeed();
        })
        .catch((error) => {
          me._circularInjectionHelper.coreLogService.error("UserHelperService.logOut()", "_circularInjectionHelper.yourAppServer.Auth.SignOut()", error);
          me._circularInjectionHelper.yourAppServer.Auth.SignInAnonymousOrNobodyIfNeed();
          reject();
        });
    });
  }

  public anonymizeCustomer(): Promise<boolean> {
    const me = this;
    return new Promise((resolve, reject) => {
      me._circularInjectionHelper.yourAppServer.Auth.AnonymizeCustomer()
        .then((response) => {
          if (response) {
            resolve(response.success);
          } else {
            resolve(false);
          }
          me._circularInjectionHelper.yourAppServer.Auth.SignInAnonymousOrNobodyIfNeed();
        })
        .catch((error) => {
          me._circularInjectionHelper.coreLogService.error("UserHelperService.anonymizeCustomer()", "_circularInjectionHelper.yourAppServer.Auth.AnonymizeCustomer()", error);
          me._circularInjectionHelper.yourAppServer.Auth.SignInAnonymousOrNobodyIfNeed();
          reject();
        });
    });
  }

  // mapper
  private mapToUserInfoViewModel(model: UserInfoData): UserInfoViewModel {
    const viewModel = new UserInfoViewModel();
    viewModel.name = model.name;
    viewModel.fname = model.fname;
    viewModel.email = model.email;
    return viewModel;
  }

  private mapToWebshopOrderDataViewModel(model: UserInfoData): WebshopOrderDataViewModel {
    const me = this;
    const viewModel = new WebshopOrderDataViewModel();
    viewModel.fname = model.fname;
    viewModel.name = model.name;
    viewModel.email = model.email;
    viewModel.prefix = model.prefix;
    viewModel.zip_code = model.zip_code;
    viewModel.bday_ymd = model.bday_ymd;
    viewModel.co = model.co; // max. 30
    viewModel.address = model.address; // max. 40
    viewModel.addition = model.addition; // max. 25
    viewModel.city = model.city; // max. 30
    viewModel.country = model.country; // max. 30
    viewModel.telephone1 = model.telephone1; // max. 20, privat
    viewModel.telephone2 = model.telephone2; // max. 20, geschäftlich
    viewModel.telephone_mobile = model.telephone_mobile; // max. 20
    viewModel.furtherAddresses = me.mapFurtherAddressesToViewModel(model.further_addresses);
    return viewModel;
  }

  private mapFurtherAddressesToViewModel(furtherAddresses: FurtherAddress[]): FurtherAddressViewModel[] {
    if (furtherAddresses && furtherAddresses.length > 0) {
      const furtherAddressViewModel: FurtherAddressViewModel[] = new Array();
      furtherAddresses.forEach(furtherAddress => {
        const viewModel = new FurtherAddressViewModel();
        viewModel.id = furtherAddress.token;
        viewModel.address_type = furtherAddress.address_type;
        viewModel.co = furtherAddress.co;
        viewModel.address = furtherAddress.address;
        viewModel.addition = furtherAddress.addition;
        viewModel.zip_code = furtherAddress.zip;
        viewModel.city = furtherAddress.city;
        viewModel.country = furtherAddress.country;
        viewModel.mobilePhone = furtherAddress.telefon;
        furtherAddressViewModel.push(viewModel);
      });
      return furtherAddressViewModel;
    } else {
      return null;
    }
  }
}
